import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  modifyModalRecoil,
  eventModifyModalRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import ModalType1 from '@/modal/ModalType1';
import { timeData } from '@/data/timeData';
import { DeviceApi } from '@/sdk/DeviceApi';
import { ModelApi } from '@/sdk/ModelApi';
import { deviceDetailRecoil } from '@/atom/DeviceAtom';
import {
  CreateDeviceEventDtoTx,
  DeleteDeviceOriginalDtoTx,
  EventCondition,
  SmsReceptionNumber,
  ModelControllerGetModelsParams,
} from '@/sdk/data-contracts';

interface EventModifyModalModalProps {
  modifyNum: number;
}

const EventModifyModal = ({ modifyNum }: EventModifyModalModalProps) => {
  const { id } = useParams<{ id: string }>();
  const modelApi = new ModelApi({ baseURL: Config.API_URL });
  const deviceApi = new DeviceApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const setEventModifyModalState = useSetRecoilState(eventModifyModalRecoil);
  const [deviceDetailState, setDeviceDetailState] =
    useRecoilState(deviceDetailRecoil);

  const [modelList, setModelList] = useState<string[]>([]);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isUse, setIsUse] = useState<boolean>(true);
  const [eventConditions, setEventConditions] = useState<EventCondition[]>([]);
  const [smsReceptionNumbers, setSmsReceptionNumbers] = useState<
    SmsReceptionNumber[]
  >([]);

  const fetchDeviceModelList = async () => {
    let modelId;

    const dto: ModelControllerGetModelsParams = {
      sortBy: 'createdAt',
      order: 'desc',
    };

    try {
      const { data } = await modelApi.modelControllerGetModels(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.models) {
        modelId = data.models.find(
          (list) => list.name === deviceDetailState?.modelName,
        )?.id;
      }
    } catch (err: any) {}

    try {
      const { data } = await modelApi.modelControllerGetModel(Number(modelId), {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setModelList(Object.keys(data.dataInformation));
    } catch (err: any) {}
  };

  const fetchEventModify = async () => {
    try {
      const { data } = await deviceApi.deviceControllerGetDeviceEventDetail(
        Number(id),
        modifyNum,
        {
          headers: {
            Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setName(data.name);
      setDescription(data.description);
      setIsUse(data.isUse);
      setEventConditions(data.eventConditions);
      setSmsReceptionNumbers(data.smsReceptionNumbers);
    } catch (err: any) {}
  };

  const handleRowPlus = (type: string) => {
    const row1 = {
      conditionData: '',
      value: 0,
      range: '이상',
      count: 1,
      cycle: 1,
    };
    const row2 = {
      name: '',
      phone: '',
      receptionTime: '24',
    };

    if (type === 'type1') setEventConditions((prev) => [...prev, row1]);
    if (type === 'type2') setSmsReceptionNumbers((prev) => [...prev, row2]);
  };

  const handleRowMinus = (type: string, index: number) => {
    if (type === 'type1') {
      const updatedType1 = eventConditions.filter((_, i) => i !== index);
      setEventConditions(updatedType1);
    } else if (type === 'type2') {
      const updatedType2 = smsReceptionNumbers.filter((_, i) => i !== index);
      setSmsReceptionNumbers(updatedType2);
    }
  };

  const handleType1InputChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
    index: number,
    fieldName: keyof EventCondition,
  ) => {
    const updatedType1 = [...eventConditions];
    updatedType1[index][fieldName] = e.target.value as never;
    setEventConditions(updatedType1);
  };

  const handleType2InputChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
    index: number,
    fieldName: keyof SmsReceptionNumber,
  ) => {
    const updatedType2 = [...smsReceptionNumbers];
    updatedType2[index][fieldName] = e.target.value as never;
    setSmsReceptionNumbers(updatedType2);
  };

  const handleSubmit = async () => {
    const type1RowsTrimCheck = eventConditions.every(
      (list) => list.conditionData.trim() !== '',
    );
    const type2RowsTrimCheck = smsReceptionNumbers.every(
      (list) => list.name.trim() !== '' && list.phone.trim() !== '',
    );

    if (
      name.trim() === '' ||
      description.trim() === '' ||
      !type1RowsTrimCheck ||
      !type2RowsTrimCheck
    ) {
      setErrorModalText('빈 칸을 확인해 주세요.');
      return;
    }

    const phoneRegex =
      /^(010\d{8}|02\d{8}|070\d{8}|0507\d{8}|1[5-9]\d{6}|043\d{7})$/;

    const smsReceptionPhoneTypeCheck = smsReceptionNumbers.every(
      (smsReceptionNumber) => phoneRegex.test(smsReceptionNumber.phone),
    );

    if (!smsReceptionPhoneTypeCheck) {
      setErrorModalText('SMS 수신 번호를 확인해 주세요.');
      return;
    }

    const dto: CreateDeviceEventDtoTx = {
      name,
      description,
      isUse,
      eventConditions,
      smsReceptionNumbers,
    };

    try {
      await deviceApi.deviceControllerUpdateEvent(Number(id), modifyNum, dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  useEffect(() => {
    fetchDeviceModelList();
    fetchEventModify();
  }, []);

  return (
    <>
      <ModalType1 width={1000}>
        <div className="title-box flex aic jcsb">
          <h1>이벤트 수정</h1>
          <button
            className="x-btn img-box"
            onClick={() => setEventModifyModalState(false)}
          >
            <img src="/img/ico_x_444.svg" alt="" />
          </button>
        </div>
        <div className="cnt-area flex fdc">
          <div className="cnt-wrap flex fdc">
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">기본 정보</h2>
                </div>
                <div className="right-box flex">
                  <h3>
                    <span className="inblock">*</span>필수 항목
                  </h3>
                </div>
              </div>
              <CS.TableType2>
                <table>
                  <tbody>
                    <tr>
                      <th>
                        이벤트명
                        <span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 487 }}
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        이벤트 설명<span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 487 }}
                          type="text"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        사용여부<span className="star-point">*</span>
                      </th>
                      <td>
                        <CS.Gap24Box>
                          <IS.CheckTextBoxType2>
                            <input
                              type="radio"
                              id="isMultipleOff"
                              name="isMultiple"
                              checked={isUse}
                              onChange={() => setIsUse(true)}
                            />
                            <label htmlFor="isMultipleOff">
                              <span className="img-box">
                                <img src="/img/ico_check_3.svg" alt="" />
                              </span>
                              <span className="text">사용</span>
                            </label>
                          </IS.CheckTextBoxType2>
                          <IS.CheckTextBoxType2>
                            <input
                              type="radio"
                              id="isMultipleOn"
                              name="isMultiple"
                              checked={!isUse}
                              onChange={() => setIsUse(false)}
                            />
                            <label htmlFor="isMultipleOn">
                              <span className="img-box">
                                <img src="/img/ico_check_3.svg" alt="" />
                              </span>
                              <span className="text">미사용</span>
                            </label>
                          </IS.CheckTextBoxType2>
                        </CS.Gap24Box>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CS.TableType2>
            </div>
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">이벤트 조건</h2>
                </div>
              </div>
              <CS.TableType1>
                <table>
                  <thead>
                    <tr>
                      <th>데이터</th>
                      <th>이벤트값</th>
                      <th>범위</th>
                      <th>발생횟수</th>
                      <th>주기</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventConditions.map((list, index) => (
                      <tr key={index}>
                        <td>
                          <IS.SelectType1 style={{ width: 150 }}>
                            <select
                              value={list.conditionData}
                              onChange={(e) =>
                                handleType1InputChange(
                                  e,
                                  index,
                                  'conditionData',
                                )
                              }
                              className="selected"
                            >
                              <option value="">데이터</option>
                              {modelList.map((model, index) => (
                                <option key={index} value={model}>
                                  {model}
                                </option>
                              ))}
                            </select>
                            <span className="img-box">
                              <img src="/img/arrow_bottom_555.svg" alt="" />
                            </span>
                          </IS.SelectType1>
                        </td>
                        <td>
                          <IS.TextInputType1
                            style={{ width: 110 }}
                            type="text"
                            value={list.value}
                            onChange={(e) => {
                              const numericValue = e.target.value.replace(
                                /\D/g,
                                '',
                              );
                              const updatedEvent = {
                                ...e,
                                target: { ...e.target, value: numericValue },
                              };
                              handleType1InputChange(
                                updatedEvent,
                                index,
                                'value',
                              );
                            }}
                          />
                        </td>
                        <td>
                          <IS.SelectType1 style={{ width: 110 }}>
                            <select
                              value={list.range}
                              onChange={(e) =>
                                handleType1InputChange(e, index, 'range')
                              }
                              className="selected"
                            >
                              <option value="이상">이상</option>
                              <option value="이하">이하</option>
                              <option value="초과">초과</option>
                              <option value="미만">미만</option>
                              <option value="동일">동일</option>
                            </select>
                            <span className="img-box">
                              <img src="/img/arrow_bottom_555.svg" alt="" />
                            </span>
                          </IS.SelectType1>
                        </td>
                        <td>
                          <IS.SelectType1 style={{ width: 110 }}>
                            <select
                              value={String(list.count)}
                              onChange={(e) =>
                                handleType1InputChange(e, index, 'count')
                              }
                              className="selected"
                            >
                              <option value="1">1회</option>
                              <option value="2">2회</option>
                              <option value="3">3회</option>
                              <option value="4">4회</option>
                              <option value="5">5회</option>
                              <option value="6">6회</option>
                              <option value="7">7회</option>
                              <option value="8">8회</option>
                              <option value="9">9회</option>
                              <option value="10">10회</option>
                            </select>
                            <span className="img-box">
                              <img src="/img/arrow_bottom_555.svg" alt="" />
                            </span>
                          </IS.SelectType1>
                        </td>
                        <td>
                          <IS.SelectType1 style={{ width: 110 }}>
                            <select
                              value={String(list.cycle)}
                              onChange={(e) =>
                                handleType1InputChange(e, index, 'cycle')
                              }
                              className="selected"
                            >
                              <option value="1">1일</option>
                              <option value="7">7일</option>
                              <option value="10">10일</option>
                            </select>
                            <span className="img-box">
                              <img src="/img/arrow_bottom_555.svg" alt="" />
                            </span>
                          </IS.SelectType1>
                        </td>
                        <td>
                          {index === 0 && (
                            <button
                              className="add-btn plus-btn"
                              onClick={() => handleRowPlus('type1')}
                            >
                              <span className="img-box">
                                <img src="/img/ico_plus_fff.svg" alt="" />
                              </span>
                            </button>
                          )}
                          {index !== 0 && (
                            <button
                              className="add-btn minus-btn"
                              onClick={() => handleRowMinus('type1', index)}
                            >
                              <span className="img-box">
                                <img src="/img/ico_minus_fff.svg" alt="" />
                              </span>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CS.TableType1>
            </div>
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">SMS 수신 번호</h2>
                </div>
              </div>
              <CS.TableType1>
                <table>
                  <thead>
                    <tr>
                      <th>성명</th>
                      <th>연락처</th>
                      <th>수신 시간</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {smsReceptionNumbers.map((list, index) => (
                      <tr key={index}>
                        <td>
                          <IS.TextInputType1
                            style={{ width: 200 }}
                            type="text"
                            value={list.name}
                            onChange={(e) =>
                              handleType2InputChange(e, index, 'name')
                            }
                          />
                        </td>
                        <td>
                          <IS.TextInputType1
                            style={{ width: 483 }}
                            type="text"
                            value={list.phone}
                            onChange={(e) => {
                              const numericValue = e.target.value.replace(
                                /\D/g,
                                '',
                              );
                              const updatedEvent = {
                                ...e,
                                target: { ...e.target, value: numericValue },
                              };
                              handleType2InputChange(
                                updatedEvent,
                                index,
                                'phone',
                              );
                            }}
                          />
                        </td>
                        <td>
                          <IS.SelectType1 style={{ width: 115 }}>
                            <select
                              value={list.receptionTime}
                              onChange={(e) =>
                                handleType2InputChange(
                                  e,
                                  index,
                                  'receptionTime',
                                )
                              }
                              className="selected"
                            >
                              {timeData.map((timeData, index) => (
                                <option key={index} value={timeData.timeValue}>
                                  {timeData.time}
                                </option>
                              ))}
                            </select>
                            <span className="img-box">
                              <img src="/img/arrow_bottom_555.svg" alt="" />
                            </span>
                          </IS.SelectType1>
                        </td>
                        <td>
                          {index === 0 && (
                            <button
                              className="add-btn plus-btn"
                              onClick={() => handleRowPlus('type2')}
                            >
                              <span className="img-box">
                                <img src="/img/ico_plus_fff.svg" alt="" />
                              </span>
                            </button>
                          )}
                          {index !== 0 && (
                            <button
                              className="add-btn minus-btn"
                              onClick={() => handleRowMinus('type2', index)}
                            >
                              <span className="img-box">
                                <img src="/img/ico_minus_fff.svg" alt="" />
                              </span>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CS.TableType1>
            </div>
          </div>
          <div className="btn-wrap">
            <BS.Btn2Type3
              type="button"
              onClick={() => setEventModifyModalState(false)}
            >
              취소
            </BS.Btn2Type3>
            <BS.Btn2Type4 type="button" onClick={handleSubmit}>
              수정
            </BS.Btn2Type4>
          </div>
        </div>
      </ModalType1>
    </>
  );
};

export default EventModifyModal;
